import React, { useState } from 'react';
import { Form, Button, Alert, Card, Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import userApi from '../../api/userApi';

const PasswordChange = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstLogin = location.state?.firstLogin;

  const validatePassword = () => {
    // Create an array to hold validation errors
    const errors = [];
    
    // Check minimum length (8 characters)
    if (newPassword.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    // Check for uppercase letters
    if (!/[A-Z]/.test(newPassword)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    // Check for lowercase letters
    if (!/[a-z]/.test(newPassword)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    // Check for digits
    if (!/\d/.test(newPassword)) {
      errors.push('Password must contain at least one digit');
    }
    
    // Check for special characters
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
      errors.push('Password must contain at least one special character');
    }
    
    // Check password match
    if (newPassword !== confirmPassword) {
      errors.push('Passwords do not match');
    }
    
    // Check if current password is required and provided
    if (!isFirstLogin && !currentPassword) {
      errors.push('Current password is required');
    }
    
    // If there are errors, set the error message and return false
    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    setLoading(true);

    // Validate password
    if (!validatePassword()) {
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting password change...');
      
      // If first login, use the first login password change endpoint
      if (isFirstLogin) {
        console.log('Using first login password change endpoint');
        await userApi.firstLoginPasswordChange(newPassword);
      } else {
        console.log('Using standard password change endpoint');
        await userApi.changePassword(currentPassword, newPassword);
      }

      // Password change successful
      console.log('Password change successful');
      setSuccess('Password changed successfully. You will be redirected to the login page.');
      
      // Clear the passwordChangeRequired flag
      localStorage.removeItem('passwordChangeRequired');
      
      // Set a flag indicating password was just changed
      localStorage.setItem('passwordJustChanged', 'true');
      
      // Instead of redirecting directly, force a full page reload
      // This will ensure that the auth state is completely refreshed
      setTimeout(() => {
        // Clear tokens before redirecting to login page
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        // Use window.location instead of navigate to force a full page reload
        window.location.href = '/login';
      }, 1500);
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header as="h4" className="text-center bg-primary text-white">
              {isFirstLogin ? 'Set New Password' : 'Change Password'}
            </Card.Header>
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert variant="success" className="mb-4">
                  {success}
                </Alert>
              )}
              
              {isFirstLogin && (
                <Alert variant="info" className="mb-4">
                  Since this is your first login, you need to set a new password.
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                {!isFirstLogin && (
                  <Form.Group className="mb-3" controlId="currentPassword">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required={!isFirstLogin}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="newPassword">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, one digit, and one special character.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="confirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    size="lg"
                    disabled={loading || success}
                  >
                    {loading ? 'Saving...' : 'Change Password'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordChange;
